import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { CacheDB } from '@shared/services/indexed-db';
import { LogsData } from '@shared/services/logs';
import { secondsUntilMidnight } from '@shared/utils';
import { jwtDecode } from 'jwt-decode';
import { catchError, map, Observable, of, switchMap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthData {
  private readonly http = inject(HttpClient);
  private readonly cacheDB = inject(CacheDB);
  private readonly logs = inject(LogsData);
  private readonly logoutUrl = `${new URL(environment.API.endpoint.default).origin}/logout`;

  /**
   * Stores the authentication token in IndexedDB.
   * @param token - The JWT token string to store.
   * @returns Observable that emits when the token is stored.
   */
  setToken(token: string): Observable<string> {
    return this.cacheDB.upsert('token', token, secondsUntilMidnight(), 'STORAGE');
  }

  /**
   * Retrieves the authentication token from IndexedDB.
   * @returns Observable emitting the token string or undefined if not found.
   */
  getToken(): Observable<string | undefined> {
    return this.cacheDB.get<string>('token', 'STORAGE');
  }

  /**
   * Checks if the user is authenticated by verifying the token's validity and expiration.
   * @returns Observable emitting true if authenticated, false otherwise.
   */
  isAuthenticated(): Observable<boolean> {
    return this.getToken().pipe(
      map((token) => {
        if (!token) {
          return false;
        }
        return this.isTokenValid(token);
      }),
      catchError(() => of(false))
    );
  }

  /**
   * Logs out the user by invalidating the server session, then clearing all local data.
   * Always clears local data even if the server call fails.
   * @returns Observable that completes when the logout process is finished.
   */
  logout(): Observable<void> {
    return this.http.post<void>(this.logoutUrl, null).pipe(
      catchError((error) => {
        this.logs.warn('AuthData', 'Server-side logout failed, clearing local data anyway', error);
        return of(undefined as void);
      }),
      switchMap(() => this.cacheDB.clearAllStores())
    );
  }

  /**
   * Validates the JWT token structure and expiration.
   * @param token - The JWT token string.
   * @returns boolean indicating if the token is valid and not expired.
   */
  private isTokenValid(token: string): boolean {
    if (!token) {
      return false;
    }

    try {
      const payload = jwtDecode(token);
      if (!payload || !payload.exp) {
        return false;
      }

      const expirationDate = new Date(0);
      expirationDate.setUTCSeconds(payload.exp);

      return expirationDate.valueOf() > new Date().valueOf();
    } catch (e) {
      this.logs.error('Error validating token:', e);
      return false;
    }
  }
}
